import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  fetchUsers,
  fetchPitchCount,
  addUser,
  updateUser,
  getTenantById,
  getUsageLog,
  getTenantFeature,
  addTeam,
  getTeam,
  updateTeam,
  scriptGeneration,
  getTeams,
  deleteTeam,
  fetchScriptCardData,
  fetchScriptDetails,
  fetchScriptDashboardOverview,
  getUserTenantById,
  updateScriptMeta,
  deleteScriptMeta,
  deleteWholeTeam,
  createUser,
  deleteTenant,
  fetchtenantsCount,
  fetchAdminUsers,
  fetchCreditUsage,
  fetchDeletedUsers,
  generatePersonalizedScriptSections,
  fetchRepInsights,
  fetchCompanyInsights,
  grantCredits,
  reassignScriptAssignment,
  fetchTargetCompanies,
  fetchCompanyContacts,
  splitGrantCredits,
  fetchAllocations,
  updateAllocation,
  tenantUsageGraphData,
} from "@/api/tenantDashboardService";
import type { CreditUsageParams } from "@/types/creditTypes";
import type {
  UpdateUserPayload,
  UsageLogsPayload,
  UseUsersParams,
} from "@/types/user";
import {
  getTenantDashboardOverview,
} from "@/api/adminDashboardService";
import type {
  Feature,
  ScriptCardDto,
  ScriptGeneration,
  ScriptGenResponse,
} from "@/types/tenantService";
import type { AxiosError } from "axios";

export const useUsers = ({
  currentPage = 1,
  pageSize = 10,
  search = "",
  role = "", // This 'role' is from the filter buttons (e.g., ADMIN, MANAGER, USER, or undefined for All)
  currentAuthRole = "", // This is the actual authenticated user's role (e.g., SUPER_ADMIN, ADMIN, MANAGER)
  tenantId = "",
  orderBy,
  order,
}: UseUsersParams & { currentAuthRole: string; tenantId?: string; orderBy?: string; order?: "asc" | "desc" }) => {
  return useQuery({
    queryKey: ["users", currentPage, pageSize, search, role, currentAuthRole, tenantId, orderBy, order],
    queryFn: () => {
      // If the authenticated user is SUPER_ADMIN and the filter is 'All' (role is undefined/empty) and no tenantId specified
      if (currentAuthRole === "SUPER_ADMIN" && !role && !tenantId) {
        return fetchAdminUsers({
          page: currentPage,
          pageSize,
          search,
          tenantId,
          orderBy,
          order,
        });
      } else {
        // Otherwise, use the standard fetchUsers with the provided role filter & tenantId
        return fetchUsers({
          page: currentPage,
          pageSize,
          search,
          role,
          tenantId,
          orderBy,
          order,
        });
      }
    },
    enabled: !!currentAuthRole,
    placeholderData: keepPreviousData,
  });
};

export const useDeletedUsers = ({
  currentPage = 1,
  pageSize = 10,
  search = "",
  orderBy,
  order,
  enabled = true,
}: {
  currentPage?: number;
  pageSize?: number;
  search?: string;
  orderBy?: string;
  order?: "asc" | "desc";
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["deletedUsers", currentPage, pageSize, search, orderBy, order],
    queryFn: () =>
      fetchDeletedUsers({
        page: currentPage,
        pageSize,
        search,
        orderBy,
        order,
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useCreateUser = () => {
  return useMutation({
    mutationFn: createUser,
  });
};

export const usePitchCount = () => {
  return useQuery({
    queryKey: ["pitch"],
    queryFn: fetchPitchCount,
  });
};

export const useAddUser = () => {
  return useMutation({
    mutationFn: addUser,
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: (user: UpdateUserPayload) => {
      const { id, ...data } = user;
      if (typeof id !== "string" || !id) {
        throw new Error("id must be a non-empty string");
      }
      const formData = new FormData();
      // Object.entries(data).forEach(([key, value]) => {
      //   if (value !== undefined) {
      //     formData.append(key, value);
      //   }
      // });
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((val) => {
              formData.append(key, val);
            });
          } else {
            formData.append(key, value);
          }
        }
      });
      return updateUser(id, formData);
    },
  });
};

export const useDeleteTenant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => deleteTenant(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenantDashboardData"] });
    },
    onError: (error: AxiosError) => {
      console.error("Failed to delete tenant:", error);
    },
  });
};

export const useTenantById = ({
  tenantId,
  query,
}: {
  tenantId: string;
  query: Record<string, string>;
}) => {
  return useQuery({
    queryKey: ["tenant", tenantId, query],
    queryFn: () => getTenantById(tenantId, query),
    enabled: !!tenantId,
  });
};

export const tenantFeature = (tenantId: string) => {
  return useQuery<Feature[]>({
    queryKey: ["tenantFeature", tenantId],
    queryFn: () => getTenantFeature(tenantId),
    enabled: !!tenantId,
  });
};

export const useUsageLogsTenant = (
  tenantId: string,
  payload: UsageLogsPayload,
  page: number,
  limit: number,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["usageLogs", tenantId, payload, page, limit],
    queryFn: () =>
      getUsageLog(tenantId, {
        ...payload,
        page,
        limit,
      }),
    enabled,
  });
};
export const useTenantDashboard = () => {
  return useQuery({
    queryKey: ["tenantDashboardData"],
    queryFn: () => getTenantDashboardOverview({ page: 1, pageSize: 1000, orderBy: "desc" }),
    retry: 1,
  });
};
export const useAddTeam = () => {
  return useMutation({
    mutationFn: addTeam,
  });
};
export const usegetTeam = (page = 1, limit = 10, search = "") => {
  return useQuery({
    queryKey: ["team", page, search],
    queryFn: () => getTeam(page, limit, search),
    // keepPreviousData: true,
  });
};

export const useUpdateTeam = () => {
  return useMutation({
    mutationFn: (user: UpdateUserPayload) => {
      const { tenantId, ...data } = user;
      if (!tenantId) throw new Error("tenantId is required");
      return updateTeam(tenantId, data);
    },
  });
};

export const useScriptGeneration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScriptGeneration) => scriptGeneration(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scriptCardData"] });
    },
  });
};

export const useGeneratePersonalizedScriptSections = () => {
  return useMutation({
    mutationFn: (payload: { userId?: string; scriptType: string; companyId?: string; companyContactId?: string; companyInsight?: string }) => generatePersonalizedScriptSections(payload),
  });
};

export const useGetRepInsights = (userId: string) => {
  return useQuery({
    queryKey: ["repInsights", userId],
    queryFn: () => fetchRepInsights(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 30, // 30 minutes cache
    gcTime: 1000 * 60 * 60, // 1 hour memory retention
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useGetCompanyInsights = (companyId: string | undefined) => {
  return useQuery({
    queryKey: ["companyInsights", companyId],
    queryFn: () => {
      if (!companyId) return Promise.resolve(null);
      return fetchCompanyInsights(companyId);
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 30, // 30 minutes cache
    gcTime: 1000 * 60 * 60, // 1 hour memory retention
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useTargetCompanies = (params?: { userId?: string; tenantId?: string } | string) => {
  const queryKey = typeof params === "string" 
    ? ["targetCompanies", params] 
    : ["targetCompanies", params?.userId, params?.tenantId];

  const enabled = typeof params === "object" ? Boolean(params?.userId) : Boolean(params);

  return useQuery<any>({
    queryKey,
    queryFn: () => fetchTargetCompanies(params),
    enabled,
  });
};

export const useCompanyContacts = (customerId?: string) => {
  return useQuery<any>({
    queryKey: ["companyContacts", customerId],
    queryFn: () => fetchCompanyContacts(customerId),
  });
};

export const useScriptDashboardOverview = (tenantId: string, enabled = true) => {
  return useQuery({
    queryKey: ["scriptDashboardOverview", tenantId],
    queryFn: () => fetchScriptDashboardOverview(tenantId),
    enabled: !!tenantId && enabled,
  });
};

export const useReassignScriptAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, assignedToId }: { assignmentId: string; assignedToId: string }) =>
      reassignScriptAssignment(assignmentId, assignedToId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scriptDashboardOverview"] });
      queryClient.invalidateQueries({ queryKey: ["scriptCardData"] });
      queryClient.invalidateQueries({ queryKey: ["scriptDetails"] });
    },
  });
};

export const useTeams = (tenantId: string, enabled = true) => {
  return useQuery({
    queryKey: ["teams", tenantId],
    queryFn: () => getTeams(tenantId),
    enabled: !!tenantId && enabled,
  });
};
export const useDeleteTeam = () => {
  return useMutation({
    mutationFn: (user: UpdateUserPayload) => {
      const { teamId, userId } = user;
      return deleteTeam(teamId, userId);
    },
  });
};

export const useScriptCardData = (
  scripttype: string,
  tenantID: string,
  page: number,
  limit: number,
  search?: string,
) => {
  return useQuery<{ data: { data: ScriptCardDto[]; total: number } }, Error>({
    queryKey: ["scriptCardData", scripttype, page, limit, search],
    queryFn: () =>
      fetchScriptCardData(
        {
          scripttype: scripttype?.toLowerCase(),
          page,
          limit,
          search,
        },
        tenantID,
      ),
    enabled: Boolean(tenantID),
    // keepPreviousData: true,
  });
};

export const useScriptDetails = (id: string) =>
  useQuery<ScriptGenResponse>({
    queryKey: ["scriptDetails", id],
    queryFn: () => fetchScriptDetails(id),
    enabled: !!id,
  });

export const useTenantUserById = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ["tenant-user", id],
    queryFn: () => getUserTenantById(id),
    enabled: !!id && enabled,
  });
};

export const useUpdateScriptMeta = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      scriptMetaId,
      payload,
    }: {
      scriptMetaId: string;
      payload: ScriptGeneration;
    }) => updateScriptMeta(scriptMetaId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scriptCardData"] });
      queryClient.invalidateQueries({
        queryKey: ["scriptDetails", variables.scriptMetaId],
      });
    },
  });
};

export const useDeleteScriptMeta = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scriptMetaId: string) => deleteScriptMeta(scriptMetaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scriptCardData"] });
    },
  });
};

export const usedeleteWholeTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) => deleteWholeTeam(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
    onError: (error: AxiosError) => {
      console.error("Failed to delete team:", error);
    },
  });
};

export const useTenants = () => {
  return useQuery({
    queryKey: ["tenants"],
    queryFn: fetchtenantsCount,
  });
};

export const useCreditUsage = (
  params: CreditUsageParams,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["creditUsage", params],
    queryFn: () => fetchCreditUsage(params),
    enabled,
  });
};

export const useGrantCredits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: grantCredits,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creditUsage"] });
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
    },
  });
};

export const useSplitGrantCredits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: splitGrantCredits,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["creditUsage"] });
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
    },
  });
};

export const useAllocations = () => {
  return useQuery({
    queryKey: ["allocations"],
    queryFn: fetchAllocations,
  });
};

export const useUpdateAllocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAllocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
      queryClient.invalidateQueries({ queryKey: ["creditUsage"] });
    },
  });
};

export const useTenantUsageGraph = (tenantId: string, filters: { type: string; year?: number; userId?: string }) => {
  return useQuery({
    queryKey: ["tenantUsageGraph", tenantId, filters],
    queryFn: () => tenantUsageGraphData(tenantId, filters),
    enabled: !!tenantId,
  });
};
